import { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useGetSubscriptionsPerProduct } from "../api/hooks";

import { DatasetItem, TooltipContent } from "./tooltips/subscriptiontooltip";
import { DateRange, DateRangeProps } from "../api/types";
import { DUMMY_SUBSCRIPTIONS } from "./walkthrough-dummy-data";

const BAR_CONTAINER_HEIGHT = 260;
const BAR_WIDTH = 30;
const BAR_GAP = 1;

const MIN_BAR_HEIGHT = 12;
const ZERO_BAR_HEIGHT = 6;

const FACILITIES_RATIO = 0.75;
const CLIENTS_RATIO = 0.25;

const FACILITIES_MAX_HEIGHT = BAR_CONTAINER_HEIGHT * FACILITIES_RATIO;
const CLIENTS_MAX_HEIGHT = BAR_CONTAINER_HEIGHT * CLIENTS_RATIO;
const DIVIDER_PERCENT = CLIENTS_RATIO * 100;

const getIconSrc = (icon?: string) => {
  if (icon && /\.(png|svg|webp|jpg|jpeg)$/i.test(icon)) {
    return icon;
  }

  return "/bizlogo.svg";
};

const getLastMonthRange = (): DateRange => {
  const now = new Date();

  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

  return {
    startDate: start,
    endDate: end,
  };
};

const SubscriptionsCard = ({ dateRange, isWalkthroughActive }: DateRangeProps & { isWalkthroughActive?: boolean }) => {
  const [isHovered, setIsHovered] = useState(false);

  const effectiveDateRange = dateRange ?? getLastMonthRange();

  const { data: realData, isLoading: realLoading, isError: realError, isSuccess: realSuccess } =
    useGetSubscriptionsPerProduct(effectiveDateRange);

  const data = isWalkthroughActive ? DUMMY_SUBSCRIPTIONS : realData;
  const isLoading = isWalkthroughActive ? false : realLoading;
  const isError = isWalkthroughActive ? false : realError;
  const isSuccess = isWalkthroughActive ? true : realSuccess;

  const dataset: DatasetItem[] =
    data?.map((item) => ({
      product: item.productName,
      facilities: item.facilities,
      clients: item.clients,
      icon: item.iconUrl ?? undefined,
    })) ?? [];

  const [animateBars, setAnimateBars] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setAnimateBars(false);
      return;
    }

    const t = setTimeout(() => setAnimateBars(true), 50);
    return () => clearTimeout(t);
  }, [isLoading]);

  const Legend = ({ color, label }: { color: string; label: string }) => (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ width: 38, height: 18, bgcolor: color, borderRadius: 6 }} />
      <Typography fontWeight={600} color="primary.main" variant="body2">
        {label}
      </Typography>
    </Stack>
  );

  const hasAnySubscriptions = dataset.some(
    (item) => item.facilities > 0 || item.clients > 0,
  );

  return (
    <Card
      id="dashboard-subscriptions-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",
        p: 3,
        borderRadius: 3,
        backgroundColor: "#eef3ff",
        minHeight: 470,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 4px 6px rgba(15, 23, 42, 0.18)",
      }}
    >
      <Typography
        fontWeight={600}
        fontSize={20}
        paddingInline={3}
        textAlign="left"
        color="primary.main"
      >
        Total Subscriptions
      </Typography>

      {/* Legend */}
      <Stack direction="row" spacing={2} mt={1} mb={3} paddingInline={5.5}>
        <Legend color="#0f69aa" label="Facilities" />
        <Legend color="#0ea9d0" label="Clients" />
      </Stack>

      <Box
        sx={{
          flex: 1,
          overflowX: "auto",
          overflowY: "auto",
          minHeight: 0,
          marginX: 4,
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isHovered ? "rgba(15,23,42,0.25)" : "transparent",
            borderRadius: 3,
            transition: "background-color 0.25s ease",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          scrollbarColor: isHovered
            ? "rgba(15,23,42,0.25) transparent"
            : "transparent transparent",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              height: "250px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={36} thickness={4} />

            <Typography fontSize={14} fontWeight={600} color="text.secondary">
              Fetching Data...
            </Typography>
          </Box>
        ) : isError ? (
          <Box
            sx={{
              height: "320px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography fontSize={16} fontWeight={600} color="error.main">
              Unable to load subscription data. Please try again.
            </Typography>
          </Box>
        ) : isSuccess && !hasAnySubscriptions ? (
          <Box
            sx={{
              height: "320px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                color: "#6D87AA",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <img
                src="/empty-table-data-logo.svg"
                alt="No Subscriptions"
                style={{ maxHeight: 240 }}
              />
              <Typography
                fontFamily="Poppins"
                fontWeight={600}
                fontSize="18px"
                color="#A1A1A1"
              >
                No subscriptions found for the selected timeframe.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: "red",
              display: "flex",
              gap: `${BAR_GAP}px`,
              gridTemplateColumns: `repeat(${dataset.length}, minmax(14px, 1fr))`,
              width: 0,
            }}
          >
            {dataset.map((item) => {
              const total = item.facilities + item.clients;

              let facilitiesHeight = ZERO_BAR_HEIGHT;
              let clientsHeight = ZERO_BAR_HEIGHT;

              if (total > 0) {
                facilitiesHeight =
                  item.facilities > 0
                    ? Math.max(
                        (item.facilities / total) * FACILITIES_MAX_HEIGHT,
                        MIN_BAR_HEIGHT,
                      )
                    : ZERO_BAR_HEIGHT;

                clientsHeight =
                  item.clients > 0
                    ? Math.max(
                        (item.clients / total) * CLIENTS_MAX_HEIGHT,
                        MIN_BAR_HEIGHT,
                      )
                    : ZERO_BAR_HEIGHT;
              }

              return (
                <Box
                  key={item.product}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      ml: 2,
                      mr: 2,
                      mt: 2,
                      height: BAR_CONTAINER_HEIGHT,
                      width: BAR_WIDTH,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: `${DIVIDER_PERCENT}%`,
                        left: "50%",
                        width: 2,
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(15,23,42,0.05)",
                        pointerEvents: "none",
                      }}
                    />

                    <Tooltip
                      disableInteractive
                      title={<TooltipContent item={item} type="facilities" />}
                      placement="top"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            padding: 0,
                          },
                        },
                        arrow: {
                          sx: {
                            color: "#020617",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: `${DIVIDER_PERCENT}%`,
                          height: facilitiesHeight,
                          width: "100%",
                          bgcolor: "#0f69aa",
                          borderRadius: "15px 15px 0 0",
                          cursor: "pointer",

                          transformOrigin: "bottom center",
                          transform: animateBars ? "scaleY(1)" : "scaleY(0)",
                          transition:
                            "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), " +
                            "box-shadow 0.25s ease",

                          "&:hover": {
                            bgcolor: "rgba(19,131,213)",
                            boxShadow:
                              "0 0 15px rgba(19,131,213,0.9), 0 0 20px rgba(19,131,213,0.6)",
                          },
                        }}
                      />
                    </Tooltip>

                    {/* Clients */}
                    <Tooltip
                      disableInteractive
                      title={<TooltipContent item={item} type="clients" />}
                      placement="bottom"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            padding: 0,
                          },
                        },
                        arrow: {
                          sx: {
                            color: "#020617",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: `${100 - DIVIDER_PERCENT}%`,
                          height: clientsHeight,
                          width: "100%",
                          bgcolor: "#0ea9d0",
                          borderRadius: "0 0 15px 15px",
                          cursor: "pointer",

                          transformOrigin: "top center",
                          transform: animateBars ? "scaleY(1)" : "scaleY(0)",
                          transition:
                            "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), " +
                            "box-shadow 0.25s ease",

                          "&:hover": {
                            bgcolor: "rgba(18,211,255)",
                            boxShadow:
                              "0 0 20px rgba(18,211,255,0.9), 0 0 40px rgba(18,211,255,0.6)",
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>

                  <Tooltip
                    disableInteractive
                    title={<TooltipContent item={item} type="icons" />}
                    placement="bottom"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "transparent",
                          boxShadow: "none",
                          padding: 0,
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#020617",
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        mt: 2,
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        backgroundColor: "#fff",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={getIconSrc(item.icon)}
                        alt={item.product}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          objectFit: "fit",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default SubscriptionsCard;
